import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useUserPickupOrders } from '@/api/pickup-orders/queries/useUserPickupOrders'
import { PkgWarehouse, UserPkg } from '@/api/user-package/types/UserPkg'
import { useConfirm } from '@/components/ConfirmDialog'
import ClientPkgIlustration from '@/components/icons/client-pkg-ilustration.svg?react'
import InfoIcon from '@/components/icons/info.svg?react'
import { useOnboardingTour } from '@/components/onboarding-tour/OnboardingTourContext'
import { SegmentedButton } from '@/components/SegmentedButton'
import { getIsSplitPkg, getPickupOrderStatus, getPkgStatus, hasPackagesToUnite } from '@/helpers/pkgUtils'
import { RoutePath } from '@/RoutePath'

import { useUserPkgs } from '../../../api/user-package/queries/useUserPkgs'
import { Spinner } from '../../../components/ui/spinner'
import { ClientPkgFilterState, ClientPkgsFilter, ViewEnum } from './components/ClientPkgsFilter'
import { PickupOrderPreview } from './components/PickupOrderPreview'
import { PkgPreview } from './components/PkgPreview'
import { UploadBarcodeDialog } from './components/UploadBarcodeDialog'
import EmptyPkgsIcon from './empty-pkgs.svg?react'

export default function ClientPackagesPage() {
  const { t } = useTranslation()
  const { data: userPkgs, isLoading: isLoadingUserPkgs } = useUserPkgs()
  const { demoPkgs, forcePkgActionsOpen, isActive: isTourActive } = useOnboardingTour()
  const effectivePkgs = demoPkgs || userPkgs
  const isShowingDemoPkgs = Boolean(demoPkgs && (!userPkgs || userPkgs.length === 0))
  const [filter, setFilter] = useState<ClientPkgFilterState>({ warehouses: [], status: 'all' })
  const [selectedTab, setSelectedTab] = useState<ViewEnum>(ViewEnum.All)
  const { data: userPickupOrders } = useUserPickupOrders()
  const [searchParams, setSearchParams] = useSearchParams()
  const pickupUploadOrderId = searchParams.get('pickupUploadOrderId')
  const { confirm, ConfirmDialog } = useConfirm()
  const navigate = useNavigate()
  const segmentedOptions = [
    { value: ViewEnum.All, label: t('packages.all') },
    { value: ViewEnum.Shipped, label: t('packages.shipped') },
  ]
  const tabPkgs = useMemo(() => {
    return effectivePkgs?.filter(pkg => {
      if (selectedTab === ViewEnum.Identify) return pkg.status === 'needs_identification'
      if (selectedTab === ViewEnum.Shipped) return pkg.isShipped
      if (selectedTab === ViewEnum.All) return !pkg.isShipped
      return true
    })
  }, [effectivePkgs, selectedTab])

  const isUniteShown = useMemo(() => hasPackagesToUnite(effectivePkgs), [effectivePkgs])

  const filteredPackages = useMemo(
    () =>
      tabPkgs?.filter(pkg => {
        if (filter.warehouses.length > 0 && !filter.warehouses.includes(pkg.warehouse)) {
          return false
        }
        if (filter.status !== 'all') {
          return getPkgStatus(pkg, t) === filter.status
        }
        if (filter.status === 'all' && selectedTab === ViewEnum.All) {
          return !pkg.isShipped
        }
        return true
      }) || [],
    [tabPkgs, filter.warehouses, filter.status, selectedTab, t]
  )

  const availableWarehouses = useMemo(() => {
    const res = new Set(tabPkgs?.map(pkg => pkg.warehouse).filter(Boolean))
    if (userPickupOrders?.length && selectedTab === ViewEnum.All) {
      res.add(PkgWarehouse.UK)
    }
    return Array.from(res)
  }, [selectedTab, tabPkgs, userPickupOrders?.length])

  const isSplitPkg = useCallback((pkg: UserPkg) => getIsSplitPkg(effectivePkgs || [], pkg), [effectivePkgs])
  const filteredPickupOrders = useMemo(() => {
    return userPickupOrders?.filter(order => {
      if (filter.warehouses.length > 0 && !filter.warehouses.includes(PkgWarehouse.UK)) {
        return false
      }
      if (filter.status !== 'all') {
        return getPickupOrderStatus(order, t) === filter.status
      }
      return true
    })
  }, [userPickupOrders, filter.warehouses, filter.status, t])

  const pkgStatuses = useMemo(() => {
    const statuses = selectedTab === ViewEnum.All ? tabPkgs?.filter(pkg => !pkg.isShipped) : tabPkgs
    const res = new Set<string>(statuses?.map(pkg => getPkgStatus(pkg, t)) || [])
    if (userPickupOrders?.length) {
      userPickupOrders.forEach(order => {
        const status = getPickupOrderStatus(order, t)
        if (status) {
          res.add(status)
        }
      })
    }
    return Array.from(res).sort()
  }, [tabPkgs, selectedTab, userPickupOrders, t])
  const pickupOrder = useMemo(() => {
    if (!pickupUploadOrderId) return null
    return userPickupOrders?.find(order => order.id === pickupUploadOrderId)
  }, [userPickupOrders, pickupUploadOrderId])

  const onCloseBarcodeDialog = () => {
    setSearchParams(prev => {
      prev.delete('pickupUploadOrderId')
      return prev
    })
  }
  const handlePackagePayment = async (pkg: UserPkg) => {
    if (pkg.isMeasureRequestActive || pkg.isCountRequestActive) {
      const shouldContinue = await confirm({
        title: t('packages.countMeasurePaymentDialogTitle'),
        description: t('packages.countMeasurePaymentDialogContent'),
        confirmLabel: t('packages.countMeasurePaymentDialogConfirm'),
        cancelLabel: t('packages.countMeasurePaymentDialogCancel'),
      })
      if (!shouldContinue) return
    }
    if (isSplitPkg(pkg)) {
      const shouldUnite = await confirm({
        title: t('packages.splitDialogTitle'),
        description: t('packages.splitDialogContent'),
        confirmLabel: t('packages.splitDialogConfirm'),
        cancelLabel: t('packages.splitDialogCancel'),
      })
      if (shouldUnite) {
        navigate(RoutePath.ClientPkgUnite)
      } else {
        navigate(`${RoutePath.ReceiptUpload}/${pkg.id}`)
      }
      return
    }
    const isConfirmed = await confirm({
      title: t('packages.splitDialogTitle'),
      description: t('packages.checkSplitDialogContent'),
      confirmLabel: t('packages.checkSplitDialogConfirm'),
      cancelLabel: t('packages.checkSplitDialogCancel'),
    })
    if (isConfirmed) {
      navigate(`${RoutePath.ReceiptUpload}/${pkg.id}`)
    }
  }
  const onSetSelectedTab = (value: ViewEnum) => {
    setSelectedTab(value)
    setFilter({ warehouses: [], status: 'all' })
  }
  return (
    <div className='mx-auto max-w-5xl px-5 py-4 md:px-6 md:py-5'>
      <div className='space-y-4'>
        <div className='flex items-center gap-4'>
          <ClientPkgIlustration />
          <div>
            <h2 className='text-text-primary text-lg font-medium'>{t('packages.title')}</h2>
            <p className='text-text-secondary text-sm'>{t('packages.subtitle')}</p>
          </div>
        </div>

        <div className='space-y-4'>
          <SegmentedButton
            options={segmentedOptions}
            value={selectedTab}
            onChange={value => onSetSelectedTab(value as ViewEnum)}
            className='w-full'
          />

          {!!tabPkgs?.length && (
            <div className='flex gap-4'>
              <ClientPkgsFilter
                statusOptions={pkgStatuses}
                warehouses={availableWarehouses}
                value={filter}
                onChange={setFilter}
                currentView={selectedTab}
                isUniteShown={!!isUniteShown}
              />
            </div>
          )}
        </div>

        {selectedTab === ViewEnum.All && !!filteredPickupOrders?.length && !isShowingDemoPkgs && (
          <div className='space-y-4'>
            {pickupOrder && <UploadBarcodeDialog order={pickupOrder} isOpen onOpenChange={onCloseBarcodeDialog} />}

            {filteredPickupOrders.map(order => (
              <PickupOrderPreview key={order.id} order={order} />
            ))}
          </div>
        )}

        <div className='space-y-4'>
          {isLoadingUserPkgs && !isShowingDemoPkgs ? (
            <div className='py-12 text-center'>
              <div className='mx-auto mb-4 flex h-24 w-24 items-center justify-center'>
                <Spinner className='h-12 w-12' />
              </div>
            </div>
          ) : filteredPackages.length === 0 ? (
            <div className='flex flex-col items-center gap-4 py-12 text-center'>
              <EmptyPkgsIcon className='' />
              <p className='text-text-secondary'>
                {selectedTab === ViewEnum.Identify ? t('packages.emptyIdentify') : t('packages.emptyShipped')}
              </p>
            </div>
          ) : (
            <>
              {selectedTab === ViewEnum.Identify && (
                <div className='rounded-lg bg-white p-4'>
                  <div className='flex items-center gap-2'>
                    <InfoIcon className='text-text-secondary' />
                    <p className='text-text-secondary text-sm'>{t('packages.identifyInfo')}</p>
                  </div>
                </div>
              )}
              {filteredPackages.map((pkg, index) => (
                <PkgPreview
                  key={pkg.id}
                  pkg={pkg}
                  currentView={selectedTab}
                  forceActionsOpen={isTourActive && forcePkgActionsOpen && index === 0}
                  onPkgPayment={isShowingDemoPkgs ? undefined : handlePackagePayment}
                  onPkgIdentify={() => {}}
                />
              ))}
            </>
          )}
        </div>
      </div>
      {ConfirmDialog}
    </div>
  )
}
